import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { Link, useLocation } from '@tanstack/react-router';
import type { ChartListItem } from '@wodi/shared';
import {
  Cell,
  Column,
  LoadingBlock,
  Notice,
  PageHeader,
  Row,
  Section,
  Table,
  TableBody,
  TableHeader,
  Tag,
} from '@wodi/ui';

import { chartListQuery } from '../api/charts.ts';
import { can, sessionQuery } from '../api/session.ts';
import { screen, TableScroll } from '../layout/screen.tsx';
import { formatInstant } from '../patients/format.ts';
import { chartStateTag, placeWords, returnPeriodWords } from './chart-words.ts';
import { LoadFailed } from './records-page.tsx';
import styles from './records.module.css';

// Charts out and overdue charts at this facility (docs/plans/phase2-medical-records-charts.md
// §6). Oldest out first. A chart is overdue when it has been out longer than the facility's
// return period (Settings > Records).

function ChartTabs() {
  const location = useLocation();
  const links = [
    { to: '/charts', label: 'Charts out' },
    { to: '/charts/overdue', label: 'Overdue charts' },
  ] as const;
  return (
    <nav aria-label="Charts" className={screen.tabs}>
      {links.map((l) => (
        <Link
          key={l.to}
          to={l.to}
          activeOptions={{ exact: true }}
          {...(location.pathname === l.to ? { 'aria-current': 'page' as const } : {})}
        >
          {l.label}
        </Link>
      ))}
    </nav>
  );
}

function ChartsTable({ items, empty }: { items: ChartListItem[]; empty: string }) {
  return (
    <TableScroll>
      <Table flat aria-label="Charts">
        <TableHeader>
          <Column isRowHeader>Chart</Column>
          <Column>Patient</Column>
          <Column>With</Column>
          <Column>Collected by</Column>
          <Column>Out since</Column>
          <Column>Due back</Column>
          <Column>State</Column>
        </TableHeader>
        <TableBody items={items} renderEmptyState={() => empty}>
          {(c) => {
            const [word, shape] = chartStateTag({ state: 'out', overdue: c.overdue });
            return (
              <Row id={c.chartId}>
                <Cell>
                  <Link to="/patients/$patientId/chart" params={{ patientId: c.patient.id }}>
                    <span className="wodi-id">{c.chartNumber}</span>
                  </Link>
                </Cell>
                <Cell>
                  {c.patient.displayName} <span className="wodi-id">{c.patient.patientNumber}</span>
                </Cell>
                <Cell>{placeWords(c.place)}</Cell>
                <Cell>{c.handledByName ?? ''}</Cell>
                <Cell>{formatInstant(c.since)}</Cell>
                <Cell>{c.dueAt === null ? '' : formatInstant(c.dueAt)}</Cell>
                <Cell>
                  <Tag state={shape}>{word}</Tag>
                </Cell>
              </Row>
            );
          }}
        </TableBody>
      </Table>
    </TableScroll>
  );
}

function ChartsView({ state }: { state: 'out' | 'overdue' }) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const allowed = session !== null && can(session, 'records.charts.read');
  const charts = useQuery({ ...chartListQuery(state), enabled: allowed });
  if (!allowed) {
    return (
      <>
        <PageHeader title="Charts" />
        <Notice tone="info">
          You don&apos;t have permission to see charts. Ask the medical records office.
        </Notice>
      </>
    );
  }
  const notSetUp = state === 'overdue' && charts.data?.returnHours === null;
  return (
    <div className={styles.page}>
      <PageHeader
        title="Charts"
        description="Paper charts issued from the records room at this facility, and where they are."
      />
      <div className={styles.toolbar}>
        <ChartTabs />
        {charts.isSuccess ? (
          <p className={styles.meta}>{returnPeriodWords(charts.data.returnHours)}</p>
        ) : null}
      </div>
      <Section
        title={state === 'out' ? 'Charts out' : 'Overdue charts'}
        description="Oldest first. To issue or return a chart, open it from the patient's record."
      >
        {charts.isPending ? <LoadingBlock label="Loading charts" rows={4} /> : null}
        {charts.isError ? (
          <LoadFailed
            error={charts.error}
            fallback="Charts couldn't be loaded."
            onRetry={() => void charts.refetch()}
          />
        ) : null}
        {notSetUp ? (
          <Notice tone="info">
            Return period not set up, so no chart is overdue yet. Set it in Settings &gt; Records.
          </Notice>
        ) : null}
        {charts.isSuccess && !notSetUp ? (
          <ChartsTable
            items={charts.data.items}
            empty={state === 'out' ? 'Every chart is in the records room.' : 'No chart is overdue.'}
          />
        ) : null}
        {charts.data?.truncated === true ? (
          <p className={styles.meta}>Showing the 500 charts out longest.</p>
        ) : null}
      </Section>
    </div>
  );
}

export function ChartsOutPage() {
  return <ChartsView state="out" />;
}

export function OverdueChartsPage() {
  return <ChartsView state="overdue" />;
}
